/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import { useEffect, useRef, useState, useMemo } from "react";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { formUsersUrl } from "@Services/dataManager";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import { renderUserTag } from "@CubeComponents/html/userTagDom";
import { useDispatch } from "react-redux";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_WARNING } from "@Services/constants";
// Unfinished SlickGrid migration, now finished: this modal renders `SlickGrid1` but took its column
// helpers from `SlickGrid`, whose `ValidatedColumnDefinition` is a distinct branded type — so the
// columns could never satisfy the grid they were handed to. Both grids' `typedColumns` are the same
// identity function (`return columns as ValidatedColumnDefinition<TRow>[]`), so taking all three from
// the grid actually rendered is behaviour-preserving. Mirrors `mailbox/.../AddUsersToMailList.tsx`.
import SlickGrid1, { typedColumns, type GridRowBase, type SlickGrid1Ref } from "@CubeComponents/html/SlickGrid1";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { FormFlowUser } from "@ServerTypes/forms/forms.schema";
/** Row type for user data */
interface UserRow extends GridRowBase {
  /** Narrows GridRowBase's generic `RowId`: this grid keys its rows by the account _id. */
  id?: ObjectIdString;
  fullName?: string;
  email?: string;
  organisation?: { name?: string };
  designation?: string;
}

/**
 * A user held in this modal's selection. `id` is the flow-user id from the wire schema — it was
 * previously declared `number`, which never matched reality: the selection is seeded from
 * `currentUsers.map((user) => ({ id: user.id, ... }))`, and a flow user's `id` is an account
 * ObjectId string. Nothing depended on the wrong declaration (every surrounding value is `any`),
 * so this is a type-level correction only. `fullName` is this grid's name for what the flow
 * persists as `name`.
 *
 * Both fields are optional because the selection has two sources with different guarantees: the
 * seed above (a flow user, whose `name` is `.nullable().optional()` on the wire) and rows pulled
 * out of the grid, which are `UserRow`s and inherit `GridRowBase`'s optional `id`.
 */
type User = Partial<Pick<FormFlowUser, "id">> & {
  fullName?: FormFlowUser["name"];
};

/**
 * The remote query the users grid is driven by. A plain type alias, not an interface, so it keeps
 * the implicit index signature SlickGrid1's `query` prop requires.
 */
type FormUsersQuery = {
  projectId?: string;
  sectionId?: string;
  selectionScope?: string;
  formId?: string;
  spaceId?: string;
  params: {
    projectId?: string;
    sectionId?: string;
    selectionScope?: string;
    spaceId?: string;
  };
};

/**
 * The `users` question this modal is picking for. `selectionType` is persisted by the form-template
 * builder (portal-settings FormsAndFields) but is NOT modelled by `questionFieldSchema` — its
 * `.catchall(z.unknown())` would type it `unknown`, which no comparison accepts. Declared here and
 * reported as a server-schema gap.
 */
interface UsersFieldData {
  selectionType?: string;
  selectionScope?: string;
}

interface AddUserProps {
  isVisible?: boolean;
  closeUsersModal: () => void;
  handleAddUserClick: (args: {
    handleCustomFieldData?: unknown;
    preSelectedUsers: User[];
    allSelectedUsers: User[];
  }) => void;
  /** Already-assigned users: seeds the selection and is disabled in the grid. */
  currentUsers?: FormFlowUser[];
  section?: { _id?: string };
  fieldData?: UsersFieldData;
  spaceId?: string;
  formId?: string;
  projectId?: string;
  /** Supplied by the form-flow editor; when absent the query below is built from the field. */
  customQuery?: FormUsersQuery;
  /** Opaque here — forwarded straight back through `handleAddUserClick`. */
  handleCustomFieldData?: unknown;
  /**
   * KNOWN BUG, preserved verbatim: the form-flow editor passes `selectionType="multipleUser"`, but
   * this modal reads the mode off `fieldData.selectionType` and never looks at this prop. It is
   * declared only so that caller keeps type-checking. The two agree today purely because the
   * fallback is also "multipleUser", so removing or honouring the prop is a behaviour change and
   * out of scope for a typing pass.
   */
  selectionType?: string;
}

const AddUser = ({
  isVisible = true,
  closeUsersModal,
  handleAddUserClick,
  currentUsers = [],
  section,
  fieldData,
  spaceId,
  formId,
  projectId,
  customQuery,
  handleCustomFieldData,
}: AddUserProps) => {
  const { t } = useTranslation();
  const [disableAddBtn, setDisableAddBtn] = useState(true);
  const dispatch = useDispatch();
  const [allSelectedUsers, setAllSelectedUsers] = useState<User[]>([]);
  const selectionMode = fieldData?.selectionType || "multipleUser";
  const dataGrid = useRef<SlickGrid1Ref<UserRow> | null>(null);

  const headerContent = () => (
    <div className=" w-full flex justify-between rounded-md">
      <div className="">{t("Add Users")}</div>
    </div>
  );

  useEffect(() => {
    const data = currentUsers.map((user) => ({ id: user.id, fullName: user.name }));
    setAllSelectedUsers(data);
  }, []);

  const footerContent = () => (
    <div className=" flex gap-4">
      <ButtonComponent
        disabled={disableAddBtn}
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        onClick={() => {
          handleAddUserClick({
            handleCustomFieldData,
            preSelectedUsers: allSelectedUsers, // patch
            allSelectedUsers,
          });
          closeUsersModal();
        }}
      >
        {t("Add")}
      </ButtonComponent>
      <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={closeUsersModal}>
        {t("Cancel")}
      </ButtonComponent>
    </div>
  );

  useEffect(() => {
    setDisableAddBtn(allSelectedUsers.length == 0);
  }, [allSelectedUsers]);

  const handleRemoveUser = (userId: User["id"]) => {
    setAllSelectedUsers((prev) => prev.filter((user) => user.id != userId));
  };

  const nameTemplate = (
    _row: number,
    _cell: number,
    _value: string | undefined,
    _columnDef: unknown,
    dataContext: UserRow,
  ) => {
    const { id } = dataContext;
    const container = document.createElement("div");
    renderUserTag(container, { id: id });
    return container;
  };

  const handleAddUserToSelection = () => {
    const selectedUsers = dataGrid.current?.dataView?.getAllSelectedItems?.() || [];
    let data = [...allSelectedUsers, ...selectedUsers];
    // `as const` makes each entry the [key, value] tuple `new Map` needs; it is erased at emit.
    let uniqueData = [...new Map(data.map((item) => [item.id, item] as const)).values()];
    if (selectionMode == "singleUser" && uniqueData.length > 1) {
      // Non-null: `length > 0` guarantees index 0 exists, which `noUncheckedIndexedAccess` cannot
      // see. Erased at emit.
      uniqueData = uniqueData.length > 0 ? [uniqueData[0]!] : [];
      dispatch(
        showToastNotification({
          content: t("Only single user can be added"),
          type: TOAST_TYPE_WARNING,
        }),
      );
    }
    setAllSelectedUsers(() => uniqueData);
  };

  const queryObj: FormUsersQuery = customQuery ?? {
    projectId,
    sectionId: section?._id,
    selectionScope: fieldData?.selectionScope,
    formId,
    params: {
      projectId,
      sectionId: section?._id,
      selectionScope: fieldData?.selectionScope,
    },
  };
  if (spaceId) {
    queryObj.spaceId = spaceId;
    queryObj.params.spaceId = spaceId;
  }

  const [query] = useState<FormUsersQuery>(queryObj);
  const columnDefinitions = useMemo(
    () =>
      typedColumns<UserRow>([
        {
          field: "fullName",
          id: "fullName",
          filterType: "Text",
          name: t("User Name"),
          width: 150,
          formatter: nameTemplate,
        },
        {
          field: "email",
          id: "email",
          filterType: "Text",
          name: t("E-Mail ID"),
          width: 150,
        },
        {
          field: "organisation",
          id: "organisation.name",
          filterType: "Text",
          name: t("Org"),
          width: 150,
          _valueGetter: (row) => row.organisation?.name,
        },
        {
          field: "designation",
          id: "designation",
          filterType: "Text",
          name: t("Designation"),
          width: 150,
        },
      ]),
    [],
  );
  const getUsersGrid = () => (
    <SlickGrid1
      ref={dataGrid}
      remoteDataSource={formUsersUrl}
      query={query}
      infiniteScroll
      enableFilteringOption
      disabledIds={currentUsers.map((u) => u.id)}
      enableSortingOption
      columnDefinitions={columnDefinitions}
    />
  );
  const modelContent = () => (
    <div className="w-full h-full overflow-auto gap-5 flex flex-col">
      <div className="flex flex-2 flex-col overflow-auto border border-button_light rounded-lg">
        <div className="border border-button_light" />
        {getUsersGrid()}
        <div className="p-4">
          <ButtonComponent onClick={handleAddUserToSelection}>{t("Add to selection")}</ButtonComponent>
        </div>
      </div>
      <div className="border flex-1 overflow-auto flex flex-col border-button_light rounded-lg">
        <div className="p-4 font-semibold">{t("Selection")}</div>
        <div className="border border-button_light" />
        <div className="flex p-4 overflow-auto flex-wrap gap-3">
          {allSelectedUsers.map((user) => (
            <UserNameTagComponent
              key={user.id}
              handleDeleteIconClicked={() => {
                handleRemoveUser(user.id);
              }}
              id={user.id}
            />
          ))}
        </div>
      </div>
    </div>
  );

  const getUserModal = () => (
    <ModalStateDialogComponent
      allowModalPadding={false}
      footerContent={footerContent()}
      width="70%"
      height="100%"
      headerTitle={headerContent()}
      onCancel={closeUsersModal}
      isVisible={isVisible}
      modelContent={modelContent()}
    />
  );

  return getUserModal();
};
export default AddUser;
